import { useWorkspaceCanvases, useWorkspaces } from "@/api/workspaces";
import { UploadTarget } from "@/hooks/useFileRecordUpload";
import { Canvas, Workspace } from "@cohesive/api-client";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@cohesive/ui-components/ui/drawer";
import {
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Layers,
  Loader2,
} from "lucide-react";
import { useState } from "react";

interface UploadTargetSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen destination; the sheet closes itself. */
  onSelect: (target: UploadTarget) => void;
  fileCount?: number;
}

/**
 * Bottom sheet to pick where mobile uploads land: a workspace (root) or a canvas
 * inside it. Returns an UploadTarget consumed by useFileRecordUpload().enqueueFiles.
 */
export function UploadTargetSheet({
  open,
  onOpenChange,
  onSelect,
  fileCount,
}: UploadTargetSheetProps) {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const { data: workspaces, isLoading } = useWorkspaces();

  const close = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) setWorkspace(null);
  };

  const choose = (target: UploadTarget) => {
    onSelect(target);
    close(false);
  };

  const title = workspace
    ? workspace.name
    : `Upload ${
        fileCount ? `${fileCount} file${fileCount > 1 ? "s" : ""}` : "files"
      } to…`;

  return (
    <Drawer open={open} onOpenChange={close}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="text-left">
          <DrawerTitle className="truncate">{title}</DrawerTitle>
        </DrawerHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-safe">
          {workspace ? (
            <CanvasList
              workspace={workspace}
              onBack={() => setWorkspace(null)}
              onChoose={choose}
            />
          ) : (
            <WorkspaceList
              workspaces={workspaces}
              isLoading={isLoading}
              onDrill={setWorkspace}
              onChoose={choose}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

const rowClass =
  "flex flex-1 items-center gap-3 rounded-lg px-3 py-3 text-left active:bg-accent";

function SheetLoading() {
  return (
    <div className="flex items-center justify-center py-8 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

function WorkspaceList({
  workspaces,
  isLoading,
  onDrill,
  onChoose,
}: {
  workspaces: Workspace[] | undefined;
  isLoading: boolean;
  onDrill: (workspace: Workspace) => void;
  onChoose: (target: UploadTarget) => void;
}) {
  if (isLoading) return <SheetLoading />;
  if (!workspaces?.length) {
    return (
      <p className="px-3 py-6 text-center text-sm text-muted-foreground">
        No workspaces yet.
      </p>
    );
  }
  // "My workspace" first (it's the default chat target — make it the one-tap
  // capture target too), then alphabetical — the switcher's ordering.
  const sorted = [...workspaces].sort((a, b) =>
    a.is_private !== b.is_private
      ? Number(b.is_private) - Number(a.is_private)
      : a.name.localeCompare(b.name),
  );
  return (
    <ul className="flex flex-col pb-2">
      {sorted.map((ws) => (
        <li key={ws.id} className="flex items-center gap-1">
          <button
            type="button"
            className={rowClass}
            onClick={() =>
              onChoose({ class: "workspace", workspace_id: ws.id })
            }
          >
            <FolderOpen className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="truncate">
              {ws.is_private ? "My workspace" : ws.name}
            </span>
          </button>
          <button
            type="button"
            className="mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground active:bg-accent"
            onClick={() => onDrill(ws)}
            aria-label={`Choose a canvas in ${ws.name}`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function CanvasList({
  workspace,
  onBack,
  onChoose,
}: {
  workspace: Workspace;
  onBack: () => void;
  onChoose: (target: UploadTarget) => void;
}) {
  const { data: canvases, isLoading } = useWorkspaceCanvases(workspace.id);
  return (
    <div className="flex flex-col pb-2">
      <button
        type="button"
        onClick={onBack}
        className="mb-1 flex items-center gap-1.5 self-start px-3 py-2 text-sm text-muted-foreground active:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Workspaces
      </button>
      <button
        type="button"
        className={rowClass}
        onClick={() =>
          onChoose({ class: "workspace", workspace_id: workspace.id })
        }
      >
        <FolderOpen className="h-5 w-5 shrink-0 text-muted-foreground" />
        <span className="font-medium">Upload to workspace</span>
      </button>
      {isLoading ? (
        <SheetLoading />
      ) : canvases?.length ? (
        <ul className="flex flex-col">
          {canvases.map((canvas: Canvas) => (
            <li key={canvas.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left active:bg-accent"
                onClick={() =>
                  onChoose({ class: "canvas", canvas_id: canvas.id })
                }
              >
                <Layers className="h-5 w-5 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  {canvas.name || "Untitled canvas"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3 py-4 text-sm text-muted-foreground">
          No canvases in this workspace.
        </p>
      )}
    </div>
  );
}
